import type { BankItem } from "../../engine/bank-types";

/**
 * 章句關聯 — the Analects is not a list of aphorisms.
 *
 * These items ask what one passage does to another: which passage answers it,
 * limits it, supplies its missing half, or is its counter-example. A student
 * who can do this stops reading the book as 512 detached sentences.
 */
export const LINKAGE_ITEMS: BankItem[] = [
  {
    id: "lk-1",
    type: "linkage",
    refs: [31, 451],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "找出為某一主張作證的章",
    stem: "2.15「思而不學則殆」若要在書中找一段孔子的親身驗證，哪一章最貼切？",
    options: [
      { id: "a", text: "15.31 終日不食終夜不寢以思，無益" },
      { id: "b", text: "2.11「溫故而知新，可以為師矣」", why: "講的是從舊出新，不涉思與學失衡的後果。" },
      { id: "c", text: "7.20「我非生而知之者，好古，敏以求之者也」", why: "講的是求知的來源，不是空思的無效。" },
      { id: "d", text: "9.19「譬如為山，未成一簣」", why: "講的是持續與中止，與思學關係無關。" },
    ],
    answerId: "a",
    explanation: "一個主張加一個親身案例，論證強度完全不同。15.31 是孔子少見的自述實驗：他真的試過只想不學，結論是行不通。",
  },
  {
    id: "lk-2",
    type: "linkage",
    refs: [86, 84],
    concepts: ["xiao"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "用一章限制另一章",
    stem: "有人引 4.20「三年無改於父之道」主張子女不該質疑父母。哪一章最能限制這種讀法？",
    options: [
      { id: "a", text: "4.18 事父母幾諫，見志不從又敬不違" },
      { id: "b", text: "4.19「父母在，不遠遊，遊必有方」", why: "講行止不講異議，限制不了「不可質疑」這個推論。" },
      { id: "c", text: "4.21「父母之年，不可不知也」", why: "講掛念，與能否勸諫無關。" },
      { id: "d", text: "2.7「今之孝者，是謂能養」", why: "區分養與敬，不涉及勸諫的正當性。" },
    ],
    answerId: "a",
    explanation: "同一篇裡兩章緊挨著：4.18 給了勸諫的位置，4.20 給了改動的時機。把它們拆開單讀，才會得出「愚孝」的結論。",
  },
  {
    id: "lk-3",
    type: "linkage",
    refs: [329, 38],
    concepts: ["xin", "zheng"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "個人之信與政治之信",
    stem: "12.7「民無信不立」與 2.22「人而無信，不知其可也」的關係是？",
    options: [
      { id: "a", text: "同一原理見於個人與政權兩個尺度" },
      { id: "b", text: "互相矛盾：一個講個人，一個講政治，標準不同", why: "尺度不同不等於矛盾；兩章用的是同一個判準。" },
      { id: "c", text: "2.22 是 12.7 的例外情況", why: "並無例外關係，二者是同一原理的兩次應用。" },
      { id: "d", text: "12.7 已經取代了 2.22 的說法", why: "《論語》非系統論著，不存在後章取代前章的體例。" },
    ],
    answerId: "a",
    explanation: "把兩章並讀，「信」就從一種私德升級為一種結構性條件：沒有它，個人接不上別人，政權接不上百姓。",
  },
  {
    id: "lk-4",
    type: "linkage",
    refs: [172, 83],
    concepts: ["xue", "zhiren"],
    difficulty: 1,
    cognitive: "analyze",
    purpose: "兩章講同一種學習動作",
    stem: "7.22「擇其善者而從之，其不善者而改之」與哪一章講的是同一件事？",
    options: [
      { id: "a", text: "4.17 見賢思齊，見不賢而內自省" },
      { id: "b", text: "1.8「過，則勿憚改」", why: "講的是對自己已犯之過的態度，不涉及以他人為鏡。" },
      { id: "c", text: "15.30「過而不改，是謂過矣」", why: "同樣是講自己的過錯，不是觀察他人。" },
      { id: "d", text: "5.27「見其過而內自訟」", why: "講自我省察的深度，缺了以他人為參照這一環。" },
    ],
    answerId: "a",
    explanation: "兩章結構完全對應：好的一面拿來跟從／看齊，不好的一面拿來改正／自省。他人無論賢與不賢，都成為可用的材料。",
  },
  {
    id: "lk-5",
    type: "linkage",
    refs: [444, 324],
    concepts: ["shu", "ren"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "同一句話出現在兩處的意義",
    stem: "「己所不欲，勿施於人」在 12.2（答仲弓問仁）與 15.24（答子貢問終身行之）都出現。這說明？",
    options: [
      { id: "a", text: "它既是仁的內容，也是終身可行之法" },
      { id: "b", text: "《論語》編纂重複，屬於失誤", why: "兩處語境與問題不同，是有意的兩次使用。" },
      { id: "c", text: "孔子只有這一句話可以回答問題", why: "他答仁的方式極多變，此句反覆出現正因其分量。" },
      { id: "d", text: "子貢與仲弓程度相同，所以答案相同", why: "兩人問的問題不同，答案相同是因為這句話覆蓋面廣。" },
    ],
    answerId: "a",
    explanation: "一句話能同時充當「仁是什麼」和「該做什麼」的答案，說明它處在原則與操作的交界處——這正是它流傳最廣的原因。",
  },
  {
    id: "lk-6",
    type: "linkage",
    refs: [102, 26],
    concepts: ["zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "由事件生出方法",
    stem: "5.10 孔子因宰予晝寢而改為「聽其言而觀其行」。這條新方法與 2.10 的關係是？",
    options: [
      { id: "a", text: "2.10 視其所以觀其所由，是其展開" },
      { id: "b", text: "兩者衝突：一個看言行，一個看內心", why: "「察其所安」正是從行為推向內心，是同一路的深化。" },
      { id: "c", text: "2.10 是原則，5.10 推翻了它", why: "5.10 是補強而非推翻，兩者方向一致。" },
      { id: "d", text: "兩章無關，分屬識人與教學兩個主題", why: "5.10 的結論明確是「吾於人也」的識人方法。" },
    ],
    answerId: "a",
    explanation: "一件小事（學生白天睡覺）逼出一條方法，而這條方法在別處有更系統的版本。把它們接起來，就看到孔子識人術的形成過程。",
  },
  {
    id: "lk-7",
    type: "linkage",
    refs: [292, 141],
    concepts: ["jiaoxue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "案例與原則的配對",
    stem: "11.22 中孔子對子路與冉有同問異答。哪一章提供了這種做法的原則性依據？",
    options: [
      { id: "a", text: "6.21 中人以下，不可以語上也" },
      { id: "b", text: "15.39「有教無類」", why: "講的是收不收，不是怎麼教。" },
      { id: "c", text: "7.8「不憤不啟，不悱不發」", why: "講的是時機，不是因人而異的內容。" },
      { id: "d", text: "17.2「性相近也，習相遠也」", why: "解釋差異的成因，不直接規定教法。" },
    ],
    answerId: "a",
    explanation: "6.21 給的是分層施教的原則，11.22 是它最生動的執行實例。原則加案例配成一對，因材施教才不只是四個字。",
  },
  {
    id: "lk-8",
    type: "linkage",
    refs: [489, 370],
    concepts: ["zhongyong", "zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "用一章解釋另一章的用詞",
    stem: "17.13「鄉愿，德之賊也」中的「鄉愿」是什麼人？哪一章的內容最能說明？",
    options: [
      { id: "a", text: "13.24 鄉人皆好之而孔子曰未可也" },
      { id: "b", text: "17.12「色厲而內荏」", why: "那是外強中乾，與八面討好不同。" },
      { id: "c", text: "15.27「巧言亂德」", why: "指向言辭，鄉愿的問題不在會不會說話。" },
      { id: "d", text: "2.14「小人比而不周」", why: "結黨營私與誰都不得罪是兩種毛病。" },
    ],
    answerId: "a",
    explanation: "13.24 提供了鄉愿的行為特徵，17.13 給出了對它的判決。合看才明白孔子為何用「賊」字——它偷走的是評價的有效性。",
  },
  {
    id: "lk-9",
    type: "linkage",
    refs: [201, 379],
    concepts: ["chushi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "同一處境下的兩種指示",
    stem: "8.13「邦無道則隱」與 14.3「邦無道，危行言孫」是否衝突？",
    options: [
      { id: "a", text: "不衝突：一講去留，一講留下如何自處" },
      { id: "b", text: "衝突：一個要走，一個要留", why: "14.3 沒有要求留下，只說明若身處其中該如何自處。" },
      { id: "c", text: "不衝突：兩章說的都是要隱退", why: "14.3「危行」明說行為仍要端正，並非隱退。" },
      { id: "d", text: "衝突：後章推翻了前章", why: "《論語》無此體例，且兩者層面不同。" },
    ],
    answerId: "a",
    explanation: "「危行言孫」是很細的分寸：行為不能妥協，說話可以低調。這在 8.13 的去留選擇之外，補上了「暫時走不了怎麼辦」的答案。",
  },
  {
    id: "lk-10",
    type: "linkage",
    refs: [43, 487],
    concepts: ["li", "ren"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "兩章共同界定禮的本質",
    stem: "3.3「人而不仁，如禮何」與 17.11「禮云禮云，玉帛云乎哉」共同說明？",
    options: [
      { id: "a", text: "禮的實質不在器物動作，而在用心" },
      { id: "b", text: "禮已經過時，不必再講", why: "孔子畢生重禮，兩章批的是空殼化，不是禮本身。" },
      { id: "c", text: "禮的形式不重要，可以任意簡化", why: "3.4 雖寧儉，仍在禮的框架內權衡，不是任意。" },
      { id: "d", text: "只有仁者才有資格行禮", why: "兩章說的是禮失去意義，不是資格限制。" },
    ],
    answerId: "a",
    explanation: "一章從內在條件說（無仁則禮無所附），一章從外在形式說（玉帛不是禮）。兩面夾住，禮的位置就清楚了。",
  },
  {
    id: "lk-11",
    type: "linkage",
    refs: [16, 439, 406],
    concepts: ["zhiren", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "同一主張的三次重複",
    stem: "1.16「不患人之不己知」、14.30「不患人之不己知，患其不能也」、15.19「君子病無能焉，不病人之不己知也」——三章重複同一主張，差異在？",
    options: [
      { id: "a", text: "所患逐次收緊，終於落回自身能力" },
      { id: "b", text: "完全相同，屬於重複記錄", why: "三章下半句各異，正是差別所在。" },
      { id: "c", text: "語氣一次比一次強烈，內容不變", why: "不只是語氣，所患的內容確實在變。" },
      { id: "d", text: "分別針對不同弟子而說", why: "章中未記對象，且差異在內容而非聽者。" },
    ],
    answerId: "a",
    explanation: "重複本身就是強調，但細看三次的下半句：先要求識人，再要求有能力，最後只留「無能」。焦慮的正當去處被一步步收緊。",
  },
  {
    id: "lk-12",
    type: "linkage",
    refs: [150, 444],
    concepts: ["ren", "shu"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "肯定式與否定式的推法",
    stem: "6.30「己欲立而立人」與 15.24「己所不欲，勿施於人」在方法上的關係是？",
    options: [
      { id: "a", text: "同一推己思路，一從正面一從反面" },
      { id: "b", text: "前者要求高，後者是它的簡化版，可以互相取代", why: "取代不了：肯定式要求主動作為，否定式只要求不作為，強度不同。" },
      { id: "c", text: "兩者對象不同，一對朋友一對外人", why: "章中並無這樣的對象區分，兩句都是通則。" },
      { id: "d", text: "前者是仁，後者是恕，兩者無關", why: "6.30 末句「能近取譬」正是恕的方法，二者同源。" },
    ],
    answerId: "a",
    explanation: "一個立底線，一個設上限。底線人人做得到，上限值得一生趨近——孔子把兩頭都給了，取用時要知道自己在哪一頭。",
  },
  {
    id: "lk-13",
    type: "linkage",
    refs: [463, 341],
    concepts: ["zheng", "yili"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "同一批評對象的兩次出現",
    stem: "16.1 孔子責備冉有助季氏伐顓臾，11.17 責備他為季氏聚斂。兩章合看說明？",
    options: [
      { id: "a", text: "冉有一再屈從權勢，責備也隨之加重" },
      { id: "b", text: "孔子對冉有一向不滿，從未肯定過他", why: "11.3 列冉有於政事科，6.8 也稱他可以從政。" },
      { id: "c", text: "兩章記的是同一件事的不同版本", why: "聚斂與伐顓臾是兩件事，時地皆異。" },
      { id: "d", text: "說明季氏才是被批評的主要對象", why: "兩章的責備都直指冉有的選擇，而非只罵季氏。" },
    ],
    answerId: "a",
    explanation: "把同一個人的記載串起來讀，《論語》就有了縱深：冉有有才幹、被孔子看重，卻一再在該說不的時候說不出口。這比單章的評語沉重得多。",
  },
  {
    id: "lk-14",
    type: "linkage",
    refs: [238, 404],
    concepts: ["ren", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "同一組話的正說與反說",
    stem: "9.29「知者不惑，仁者不憂，勇者不懼」與 14.28「君子道者三，我無能焉：仁者不憂，知者不惑，勇者不懼」的差別是？",
    options: [
      { id: "a", text: "同樣三句，一次立標準，一次自省" },
      { id: "b", text: "後者是前者的修訂，孔子改變了看法", why: "內容一字未改，改的是說話的位置。" },
      { id: "c", text: "前者講君子，後者講聖人", why: "14.28 明說「君子道者三」，對象相同。" },
      { id: "d", text: "兩者無關，只是巧合相似", why: "三句完全相同，不可能是巧合。" },
    ],
    answerId: "a",
    explanation: "同一組話，從教人變成自省，分量完全不同。子貢那句「夫子自道也」是全書最會聽話的一次註腳——他聽出老師在說自己。",
  },
  {
    id: "lk-15",
    type: "linkage",
    refs: [147, 337],
    concepts: ["xue", "li"],
    difficulty: 1,
    cognitive: "understand",
    purpose: "書中重出章的處理",
    stem: "「博學於文，約之以禮，亦可以弗畔矣夫」在 6.27 與 12.15 兩次出現。楊伯峻的處理方式是？",
    options: [
      { id: "a", text: "後出處註明見雍也篇，不重複作注" },
      { id: "b", text: "認定其中一處是衍文，予以刪去", why: "他保留了兩處，只在注中互見。" },
      { id: "c", text: "認為兩處字句不同，分別解說", why: "兩處字句相同，故無須分別解說。" },
      { id: "d", text: "合併為一章處理", why: "章次仍分列於兩篇，未作合併。" },
    ],
    answerId: "a",
    explanation: "《論語》成書於眾手，重出並不罕見。知道這一點，讀到似曾相識的句子時就不會以為自己記錯——這也是版本意識的起點。",
  },
  {
    id: "lk-16",
    type: "linkage",
    refs: [352, 359, 339],
    concepts: ["zheng"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "三章圍繞同一命題",
    stem: "13.6「其身正，不令而行」、13.13「苟正其身矣，於從政乎何有」、12.17「政者，正也」——三章的共同命題是？",
    options: [
      { id: "a", text: "治理的效力來自治理者自身的端正" },
      { id: "b", text: "為政需要正式的名分與職位", why: "那是正名的話題（13.3），三章講的是自身端正。" },
      { id: "c", text: "為政應當嚴格執法，賞罰分明", why: "三章都不談賞罰，且 2.3 對刑政有所保留。" },
      { id: "d", text: "為政者應當多聽取百姓意見", why: "三章未涉及此，主題是正己。" },
    ],
    answerId: "a",
    explanation: "同一命題在《顏淵》《子路》兩篇反覆出現三次，且一次比一次直白，最後濃縮成「政者，正也」五個字——這是全書最強的政治定義。",
  },
  {
    id: "lk-17",
    type: "linkage",
    refs: [123, 277],
    concepts: ["xue", "jiaoxue"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "兩次問好學的答案一致",
    stem: "哀公問「弟子孰為好學」（6.3）與季康子問同一問題（11.7），孔子的回答有何一致之處？",
    options: [
      { id: "a", text: "都只舉顏回一人，且都說今也則亡" },
      { id: "b", text: "都列舉了多位弟子的長處", why: "兩次都只提顏回，未作列舉。" },
      { id: "c", text: "都回答說弟子們各有所長，難以評比", why: "他給了明確且唯一的答案。" },
      { id: "d", text: "都拒絕回答國君的提問", why: "兩次都作了正面回答。" },
    ],
    answerId: "a",
    explanation: "六百多章裡，孔子只用「好學」稱許過顏回一人，還兩次當著執政者的面說「今也則亡」。這個詞在他嘴裡的重量，由此可以稱出來。",
  },
  {
    id: "lk-18",
    type: "linkage",
    refs: [220, 147],
    concepts: ["jiaoxue", "xue"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "學生的描述印證老師的方法",
    stem: "顏淵說孔子「博我以文，約我以禮」（9.11）。這句話印證了哪一章所述的方法？",
    options: [
      { id: "a", text: "6.27 博學於文，約之以禮" },
      { id: "b", text: "7.8「不憤不啟，不悱不發」", why: "那是啟發時機，顏淵此處說的是博約兩步。" },
      { id: "c", text: "15.39「有教無類」", why: "講收生範圍，與教學方法無關。" },
      { id: "d", text: "2.11「溫故而知新」", why: "講從舊出新，不是博與約的配合。" },
    ],
    answerId: "a",
    explanation: "老師講原則，學生說「我就是這樣被教的」——這種內外印證在《論語》裡很少見，也讓「博文約禮」從主張變成了可考的事實。",
  },
  {
    id: "lk-19",
    type: "linkage",
    refs: [71, 166],
    concepts: ["yili"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "原則與其生活版本",
    stem: "4.5「不以其道得之，不處也」與 7.16「不義而富且貴，於我如浮雲」的關係是？",
    options: [
      { id: "a", text: "同一原則，一為判準陳述，一為實感" },
      { id: "b", text: "前者容許富貴，後者反對富貴，二者衝突", why: "7.16 限定在「不義而」三字，並未反對富貴本身。" },
      { id: "c", text: "後者是前者的例外情況", why: "沒有例外關係，兩者判準相同。" },
      { id: "d", text: "前者對君子說，後者是孔子自述，標準不同", why: "自述正是以身作則，標準並無二致。" },
    ],
    answerId: "a",
    explanation: "「如浮雲」三字是 4.5 的體感版：不是咬牙拒絕，是根本不覺得那與自己有關。原則說得清楚，感受寫得傳神，兩者缺一都不完整。",
  },
  {
    id: "lk-20",
    type: "linkage",
    refs: [190, 373],
    concepts: ["li", "ren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "德目需要條件才成立",
    stem: "8.2「勇而無禮則亂，直而無禮則絞」與 13.27「剛、毅、木、訥近仁」共同顯示孔子論德目的什麼特點？",
    options: [
      { id: "a", text: "單項品質不足以成德，須有所配合" },
      { id: "b", text: "剛毅木訥四者優於恭慎勇直四者", why: "兩章不是在排名，是在說明條件。" },
      { id: "c", text: "孔子最重視的德目是禮", why: "8.2 用禮作條件，13.27 則不涉禮，不能推出禮最重要。" },
      { id: "d", text: "德目之間互相衝突，難以兼備", why: "說的是需要配合，不是互相排斥。" },
    ],
    answerId: "a",
    explanation: "「近仁」二字用得極謹慎：剛毅木訥的人比較容易接近仁，但也只是接近。孔子談德行很少下絕對判斷，這是他一貫的分寸。",
  },
  {
    id: "lk-21",
    type: "linkage",
    refs: [296, 118],
    concepts: ["chushi", "junzi"],
    difficulty: 2,
    cognitive: "analyze",
    purpose: "兩次言志的對照",
    stem: "5.26（顏淵季路侍，各言爾志）與 11.26（四子侍坐言志）都是言志之章。兩者的共同結構是？",
    options: [
      { id: "a", text: "各言其志，由答案顯出性情與格局" },
      { id: "b", text: "孔子考核弟子，評定高下並排名", why: "他未作排名；11.26 只說「亦各言其志也已矣」。" },
      { id: "c", text: "弟子們互相辯論，孔子居中裁決", why: "兩章弟子各說各的，並無辯論。" },
      { id: "d", text: "孔子先立標準，弟子照標準回答", why: "他明說「毋吾以也」，正是不設限。" },
    ],
    answerId: "a",
    explanation: "兩章都是難得的群像。5.26 裡子路要車馬共敝、顏淵要無伐善無施勞、孔子要老者安之——三句話三種格局，勝過任何評語。",
  },
  {
    id: "lk-22",
    type: "linkage",
    refs: [497, 24],
    concepts: ["xiao", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "由制度爭議回到心之安否",
    stem: "宰我質疑三年之喪太久，孔子問「食夫稻，衣夫錦，於女安乎？」這一問把爭論轉到了哪裡？",
    options: [
      { id: "a", text: "從制度的合理性，轉到心安與否" },
      { id: "b", text: "從三年轉到一年，討論具體時長", why: "孔子沒有討價還價，他換了問題的層面。" },
      { id: "c", text: "從喪禮轉到飲食服飾的規定", why: "稻與錦只是舉例，用來測試心安。" },
      { id: "d", text: "從宰我個人轉到全體弟子", why: "追問始終針對宰我本人「於女安乎」。" },
    ],
    answerId: "a",
    explanation: "宰我答「安」，孔子說「女安則為之」——放他去做，卻在他出去後說「予之不仁也」。這一章把禮的根據徹底交還給了人心，也因此最見鋒芒。",
  },
];
